import Globals, { FEATURE_FLAGS } from "@/data/globals";
import {
  GainType,
  GameEngines,
  GameSystems,
  IDataSpecialRule,
  ISelectedUnit,
  ISpecialRule,
  IUnit,
  IUpgradeGains,
  IUpgradeGainsItem,
  IUpgradeGainsRule,
  IUpgradeGainsWeapon,
  IUpgradeOption,
  IUpgradePackage,
  IUpgradeSection,
  IWarfleetsUnit,
  RangersStatus,
  ShipClass,
  UnitType
} from "@/data/types";
import { RuleCostFlags } from "@/data/types/rules";
import { env, isFeatureEnabled } from "@/env";
import { orderBy } from "lodash-es";
import pluralize from "pluralize";
import { Result } from "typescript-result";
import { GameSystemService } from "./GameSystemService";
import TraitService from "./rules/TraitService";

const ENABLE_V360 = isFeatureEnabled(FEATURE_FLAGS.V3_6);

export function makeCopy<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export const isServer = () => typeof window === "undefined";

export const ENTER_KEY: number = 13;
export const ESCAPE_KEY: number = 27;

export function pluralizeWrap(input: string, count: number = 2) {
  if (count < 2) return input; // Stop singularising

  let value = pluralize(input, count);
  if (count === undefined || (count > 1 && input !== value)) {
    const last = value[value.length - 1];
    if (last === "S") {
      value = value.substring(0, value.length - 1) + last.toLowerCase();
    }
  }
  return value;
}

export const isHero = (unit: { rules: { name: string }[] }) =>
  unit.rules.some((r) => r.name === "Hero" || r.name === "Unique Hero");

export function calculateListTotal(list: ISelectedUnit[]) {
  // TODO: Campaign unit XP
  return list.reduce((value, current) => value + calculateUnitTotal(current), 0);
}
export function calculateUnitTotal(unit: ISelectedUnit) {
  if (!unit) return 0;
  let cost = unit.cost;

  for (const option of unit.selectedUpgrades.map((su) => su.option)) {
    const optionCost = option.costs?.find((x) => x.unitId === unit.id)?.cost ?? (option as any).cost ?? 9999;
    if (optionCost) {
      cost += optionCost;
    }
  }

  if (isHero(unit) && unit.traits?.length > 0) {
    const allTraitDefinitions = TraitService.getTraitDefinitions();

    const isInjury = (trait: string) => !!allTraitDefinitions?.injuries.find((x) => x.name === trait);
    const isTalent = (trait: string) => !!allTraitDefinitions?.talents.find((x) => x.name === trait);

    let injuryCount = 0;
    let talentCount = 0;
    for (let trait of unit.traits) {
      if (isInjury(trait)) injuryCount++;
      else if (isTalent(trait)) talentCount++;
    }

    cost += 5 * talentCount - 5 * injuryCount;
  }

  const isBattleScale = Globals.gameSystem === "gf" || Globals.gameSystem === "aof";
  const levelCost = isBattleScale ? (isHero(unit) ? 55 : 25) : isHero(unit) ? 15 : 5;
  const level = Math.floor(unit.xp / 5);
  if (level > 0) {
    cost += level * levelCost;
  }

  return cost;
}

export function forEachTarget(section: IUpgradeSection, iterator: (target: string, count?: number) => void) {
  for (const target of section.targets || []) {
    const count = /^(?<count>\d+)x\s/.exec(target)?.groups.count;
    const rawTarget = target.replace(/\d+x\s+/, "").trim();
    iterator(rawTarget, count ? parseInt(count) : 1);
  }
}

export const forEachOption = (
  book: { upgradePackages: IUpgradePackage[] },
  cb: (pkg: IUpgradePackage, section: IUpgradeSection, option: IUpgradeOption) => boolean | void
) => {
  for (const pkg of book.upgradePackages)
    for (const section of pkg.sections)
      for (const option of section.options) {
        if (cb(pkg, section, option)) return true;
      }

  return false;
};

export const forEachGain = (
  book: { upgradePackages: IUpgradePackage[] },
  cb: (
    pkg: IUpgradePackage,
    section: IUpgradeSection,
    option: IUpgradeOption,
    gain: IUpgradeGains
  ) => boolean | void,
  includeItems: boolean = true
) => {
  return forEachOption(book, (pkg, s, opt) => {
    for (const gain of opt.gains) {
      if (cb(pkg, s, opt, gain)) return true;
      if (includeItems && isItem(gain)) {
        for (const c of gain.content) {
          if (cb(pkg, s, opt, c)) return true;
        }
      }
    }
    return false;
  });
};

export const forEachUpgrade = (
  book: { upgradePackages: IUpgradePackage[] },
  unit: IUnit,
  cb: (
    pkg: IUpgradePackage,
    section: IUpgradeSection,
    option: IUpgradeOption,
    gain: IUpgradeGains
  ) => boolean | void,
  includeItems: boolean = true
) => {
  const upgradePackages = book.upgradePackages.filter((pkg) => unit.upgrades.includes(pkg.uid));

  return forEachOption({ upgradePackages }, (pkg, s, opt) => {
    for (const gain of opt.gains) {
      if (cb(pkg, s, opt, gain)) return true;
      if (includeItems && isItem(gain)) {
        for (const c of gain.content) {
          if (cb(pkg, s, opt, c)) return true;
        }
      }
    }
    return false;
  });
};

type UpgradeIdentifier = { pkgId: string; sectionId: string; optionId: string };

export const processUnitUpgrades = (
  settings: {
    handleRule?: (rule: IUpgradeGainsRule, isUpgrade: boolean) => void;
    handleWeapon?: (weapon: IUpgradeGainsWeapon, isUpgrade: boolean) => void;
    handleItem?: (item: IUpgradeGainsItem, isUpgrade: boolean, identifier?: UpgradeIdentifier) => void;
    handleOption?: (option: IUpgradeOption) => void;
    options?: {
      includeItems?: boolean;
      includeBaseUnit?: boolean;
      filterPackages?: (packages: IUpgradePackage[]) => IUpgradePackage[];
    };
  },
  armyBook: { upgradePackages: IUpgradePackage[] },
  unit: IUnit //{ upgrades: string[]; rules: ISpecialRule[]; items: IUpgradeGainsItem[] }
) => {
  const { handleRule, handleItem, handleWeapon, handleOption, options } = settings;
  const { includeItems = true, includeBaseUnit, filterPackages } = options || {};
  const filteredPackages = filterPackages
    ? filterPackages(armyBook.upgradePackages)
    : armyBook.upgradePackages;
  const packages = unit
    ? filteredPackages.filter((pkg) => unit.upgrades.includes(pkg.uid))
    : filteredPackages;

  if (includeBaseUnit) {
    for (const weapon of unit.weapons) {
      handleWeapon?.(weapon, false);
    }
    for (const rule of unit.rules) {
      handleRule?.(rule as any, false);
    }
    for (const item of unit.items) {
      handleItem?.(item, false);
      if (includeItems) {
        for (const c of item.content) {
          if (isRule(c)) {
            handleRule?.(c, false);
          } else if (isWeapon(c)) {
            handleWeapon?.(c, false);
          }
        }
      }
    }
  }

  for (const pkg of packages) {
    for (const section of pkg.sections) {
      for (const option of section.options) {
        const identifier = { pkgId: pkg.uid, sectionId: section.uid, optionId: option.uid };
        handleOption?.(option);
        for (const gain of option.gains) {
          if (isRule(gain)) {
            handleRule?.(gain, true);
          } else if (isWeapon(gain)) {
            handleWeapon?.(gain, true);
          } else if (isItem(gain)) {
            handleItem?.(gain, true, identifier);
            if (includeItems) {
              for (const c of gain.content) {
                if (isRule(c)) {
                  handleRule?.(c, true);
                } else if (isWeapon(c)) {
                  handleWeapon?.(c, true);
                }
              }
            }
          }
        }
      }
    }
  }
};

export function isItem(x: IUpgradeGains): x is IUpgradeGainsItem {
  return x.type === GainType.Item;
}

export function isWeapon(x: IUpgradeGains): x is IUpgradeGainsWeapon {
  return x.type === GainType.Weapon || (x as any).attacks > 0;
}

export function isRule(x: IUpgradeGains): x is IUpgradeGainsRule {
  return x.type === GainType.Rule;
}

export function getUnitWeapons(unit: { weapons: IUpgradeGainsWeapon[]; items: IUpgradeGainsItem[] }) {
  const itemWeapons = unit.items?.flatMap((x) => weaponsFromItem(x)) ?? [];
  return (unit.weapons ?? []).concat(itemWeapons);
}

export function getUnitRules(
  unit: { rules: ISpecialRule[]; items?: IUpgradeGainsItem[] },
  excludeItems: boolean = false
) {
  const itemRules = unit.items?.flatMap((x) => rulesFromItem(x)) ?? [];
  return (unit.rules ?? []).concat(excludeItems ? [] : itemRules);
}

function rulesFromItem(x: IUpgradeGainsItem) {
  return x.content.filter((x) => isRule(x)) as IUpgradeGainsRule[];
}

function weaponsFromItem(x: IUpgradeGainsItem): IUpgradeGainsWeapon[] {
  return x.content.filter((x) => isWeapon(x)) as IUpgradeGainsWeapon[];
}

export function displayDate(value, prefix = false) {
  const now = new Date();
  const yesterday = (() => {
    const x = new Date();
    x.setDate(x.getDate() - 1);
    return x;
  })();
  const date = new Date(value);
  if (date.toDateString() === now.toDateString()) return "today";
  if (date.toDateString() === yesterday.toDateString()) return "yesterday";
  return (prefix ? "on " : "") + date.toLocaleDateString();
}

export function timeOnly(value) {
  const date = new Date(value);
  return `${date.getHours()}:${date.getMinutes().toString().padStart(2, "0")}`;
}
export function arrayMove<T>(arr: T[], fromIndex: number, toIndex: number) {
  const element = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, element);
  return arr;
}

export const toTitleCase = (str) => {
  if (str.length === 0) return str; // If the string is empty, return it as is
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getRandomFrom = (array: any[]) => array[Math.floor(Math.random() * array.length)];

export function enrichAlias(alias: IDataSpecialRule, parent: IDataSpecialRule): IDataSpecialRule {
  if (!parent) return alias;

  const isBoostRule = (x) => x.name.split(/\s/).includes("Boost");
  const isBoost = isBoostRule(parent) && isBoostRule(alias);
  const isEnhancedRule = (x) => x.name.split(/\s/).includes("Enhanced");
  const isEnhanced = isEnhancedRule(parent) && isEnhancedRule(alias);
  const description = isEnhanced
    ? (() => {
        const parentEnhancedRule = parent.name.split("Enhanced")[1].trim().replace(" Aura", "");
        const aliasEnhancedRule = alias.name.split("Enhanced")[1].trim().replace(" Aura", "");

        return parent.description.replace(new RegExp(parentEnhancedRule, "g"), aliasEnhancedRule);
      })()
    : isBoost
      ? (() => {
          const parentBoostingRule = parent.name.split("Boost")[0].trim().replace(" Aura", "");
          const aliasBoostingRule = alias.name.split("Boost")[0].trim().replace(" Aura", "");

          return parent.description.replace(new RegExp(parentBoostingRule, "g"), aliasBoostingRule);
        })()
      : parent.description;

  return {
    ...parent,
    description,
    id: alias.id,
    aliasedRuleId: alias.aliasedRuleId,
    name: alias.name,
    archived: alias.archived,
    originalName: parent.name,
    replaceWith: alias.replaceWith,
    // Aliases can never be base or common themselves
    coreType: null
    // TODO: Which army books are using an alias?
    //armyBooks: [{ uid: x.armyBookUid, name: x.armyName }] // remove armyBooks from parent
  };
}

export function cleanObject<T>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined && value !== null)
  ) as Partial<T>;
}

export function flagEnabled(flags: number, test: number) {
  return (flags & test) === test;
}

export function isValidJson(input: string): boolean {
  try {
    JSON.parse(input);
    return true;
  } catch {
    return false;
  }
}

export function halfNumberRange(number: number | string) {
  const num = parseFloat(number.toString());
  return (num / 2).toFixed(num % 2 === 0 ? 0 : 1);
}

export function getCdnUrl(path: string) {
  if (!path) return path;
  //if (path?.startsWith("http")) return path; // Already a full URL
  const pathOnly = path.replace("https://af-images.s3.eu-north-1.amazonaws.com/", "");
  const url = env.NEXT_PUBLIC_R2_DOMAIN + "/" + pathOnly;
  //console.log("getCdnUrl", url);
  return url;
}

export function getFlagsAsNames(flags: number): string[] {
  const enabledFlagNames: string[] = [];

  for (const flag in RuleCostFlags) {
    const flagValue = RuleCostFlags[flag as keyof typeof RuleCostFlags];
    if (typeof flagValue === "number" && (flags & flagValue) === flagValue) {
      enabledFlagNames.push(flag);
    }
  }

  return enabledFlagNames;
}

export function ruleFlagEnabled(flags: number, test: RuleCostFlags) {
  return (flags & test) === test;
}

interface Change {
  property: string;
  before?: any;
  after?: any;
}

export function compareObjects(
  obj1: any,
  obj2: any,
  ignoreProps: string[] = [],
  pathSeparator: string = "."
): Change[] {
  const changes: Change[] = [];

  // Recursive function to compare nested objects
  function compareNestedObjects(nestedObj1: any, nestedObj2: any, path: string = "") {
    // Iterate over keys in nestedObj1
    for (const key in nestedObj1) {
      if (ignoreProps.includes(key)) continue;
      const fullPath = path ? `${path}${pathSeparator}${key}` : key;
      if (nestedObj1.hasOwnProperty(key)) {
        // Check for keys in obj1 not present in obj2
        if (!nestedObj2.hasOwnProperty(key)) {
          changes.push({ property: fullPath, before: nestedObj1[key] });
        } else {
          // Compare values for keys present in both objects
          if (typeof nestedObj1[key] === "object" && typeof nestedObj2[key] === "object") {
            if (Array.isArray(nestedObj1[key]) && Array.isArray(nestedObj2[key])) {
              const arr1 = nestedObj1[key];
              const arr2 = nestedObj2[key];
              const keySelector = (x) => x.id ?? x.uid ?? x.name ?? x.key ?? x.unitId ?? JSON.stringify(x);
              const labelSelector = (x) => x.label ?? x.name ?? x.hint ?? JSON.stringify(x);
              //console.log("Compare arrays...");
              for (const obj1Item of arr1) {
                if (typeof obj1Item === "object") {
                  const obj1Key = keySelector(obj1Item);
                  const obj2Item = arr2.find((x) => keySelector(x) === obj1Key);
                  if (obj2Item) {
                    compareNestedObjects(
                      obj1Item,
                      obj2Item,
                      fullPath + pathSeparator + labelSelector(obj1Item)
                    );
                  } else {
                    changes.push({ property: fullPath, after: obj1Item });
                  }
                } else {
                  const obj2Item = arr2[arr1.indexOf(obj1Item)];
                  if (obj1Item !== obj2Item) {
                    changes.push({ property: fullPath, before: obj2Item, after: obj1Item });
                  }
                }
              }

              for (const obj2Item of arr2) {
                if (typeof obj2Item === "object") {
                  const obj2Key = keySelector(obj2Item);
                  const obj1Item = arr1.find((x) => keySelector(x) === obj2Key);
                  if (!obj1Item) {
                    changes.push({ property: fullPath, before: obj2Item });
                  }
                } else {
                  const obj1Item = arr1[arr2.indexOf(obj2Item)];
                  if (obj1Item !== obj2Item) {
                    changes.push({ property: fullPath, before: obj2Item, after: obj1Item });
                  }
                }
              }
            } else {
              compareNestedObjects(nestedObj1[key], nestedObj2[key], fullPath);
            }
          } else if (nestedObj1[key] !== nestedObj2[key]) {
            changes.push({ property: fullPath, before: nestedObj1[key], after: nestedObj2[key] });
          }
        }
      }
    }

    // Check for keys in obj2 not present in obj1 and add them as added keys
    for (const key in nestedObj2) {
      if (ignoreProps.includes(key)) continue;
      const fullPath = path ? `${path}.${key}` : key;
      if (nestedObj2.hasOwnProperty(key) && !nestedObj1.hasOwnProperty(key)) {
        changes.push({ property: fullPath, before: undefined, after: nestedObj2[key] });
      }
    }
  }

  compareNestedObjects(obj1, obj2);

  return changes;
}

export function isServerSave(save: any) {
  return !save.list.units && !save.list.ships;
}

export const defaultAdminSearch = (terms: string[], item: { name: string }) => {
  const name = item.name.toLowerCase();
  for (const term of terms) {
    if (!name.includes(term)) return false;
  }

  return true;
};

export function getUnitCategories(
  units: IUnit[],
  gameSystem: GameSystems,
  showNarrativeHeroes: boolean,
  disableSort = false,
  engine: GameEngines = GameEngines.Core
) {
  if (engine === GameEngines.Core) {
    return getCoreUnitCategories(units, gameSystem, showNarrativeHeroes, disableSort);
  }
  if (engine === GameEngines.Warfleets) {
    return getWarfleetsUnitCategories(units as IWarfleetsUnit[], showNarrativeHeroes, disableSort);
  }
}

export function getCoreUnitCategories(
  units: IUnit[],
  gameSystem: GameSystems,
  showNarrativeHeroes: boolean,
  disableSort = false
) {
  const isQuest = GameSystemService.isQuest(GameSystemService.findById(gameSystem));
  const isSkirmish = GameSystemService.isSkirmish(GameSystemService.findById(gameSystem));

  // Group army units by category
  const isTough = (u: IUnit, threshold) =>
    u.rules.some((r) => {
      if (r.name !== "Tough") return false;
      const toughness = parseInt(r.rating as any) / (isQuest ? 3 : 1);
      return toughness >= threshold;
    });
  const hasRule = (u: IUnit, rule: string) => u.rules.some((r) => r.name === rule);

  const isNarrative = (u: IUnit) => (u as any).isNarrative === true || u.type === UnitType.NarrativeHero;

  const unitGroups = {
    "Narrative Heroes": [],
    Heroes: [],
    "Core Units": [],
    "Light Vehicles / Small Monsters": [],
    "Vehicles / Monsters": [],
    Artillery: [],
    Titans: [],
    Aircraft: []
  };

  const enableTriplify = isSkirmish && ENABLE_V360;
  const multiplier = enableTriplify ? 3 : 1;

  for (let unit of units.filter((x) => showNarrativeHeroes || !isNarrative(x))) {
    if (isNarrative(unit)) unitGroups["Narrative Heroes"].push(unit);
    else if (hasRule(unit, "Hero") && !isNarrative(unit)) unitGroups["Heroes"].push(unit);
    // Battle has additional categories
    else if (hasRule(unit, "Aircraft")) unitGroups["Aircraft"].push(unit);
    else if (hasRule(unit, "Entrenched") || hasRule(unit, "Artillery") || hasRule(unit, "Mobile Artillery"))
      unitGroups["Artillery"].push(unit);
    else if (isTough(unit, 18 * multiplier) && unit.size === 1) unitGroups["Titans"].push(unit);
    else if (isTough(unit, 9 * multiplier) && unit.size === 1) unitGroups["Vehicles / Monsters"].push(unit);
    else if (isTough(unit, 6 * multiplier) && unit.size === 1)
      unitGroups["Light Vehicles / Small Monsters"].push(unit);
    else unitGroups["Core Units"].push(unit);
  }

  if (showNarrativeHeroes == false) {
    delete unitGroups["Narrative Heroes"];
  }

  if (!disableSort) {
    // Order units within their groups
    for (const key of Object.keys(unitGroups)) {
      const group = unitGroups[key];
      const isHeroGroup = key.includes("Hero");
      if (isHeroGroup) {
        unitGroups[key] = orderBy(group, "cost", "desc");
      } else {
        unitGroups[key] = orderBy(group, ["size", "cost"], ["desc", "asc"]);
      }
    }
  }
  return unitGroups;
}

export function getWarfleetsUnitCategories(
  units: IWarfleetsUnit[],
  showNarrativeHeroes: boolean,
  disableSort = false
) {
  const isNarrative = (u: IUnit) => (u as any).isNarrative === true || u.type === UnitType.NarrativeHero;

  const unitGroups = {
    "Narrative Heroes": [],
    Heavy: [],
    Medium: [],
    Light: [],
    Squadrons: [],
    Other: []
  };

  for (let unit of units.filter((x) => showNarrativeHeroes || !isNarrative(x))) {
    if (isNarrative(unit)) unitGroups["Narrative Heroes"].push(unit);
    else if (unit.class === ShipClass.Heavy) unitGroups["Heavy"].push(unit);
    else if (unit.class === ShipClass.Medium) unitGroups["Medium"].push(unit);
    else if (unit.class === ShipClass.Light) unitGroups["Light"].push(unit);
    else if (unit.class === ShipClass.Squadron) unitGroups["Squadrons"].push(unit);
    else unitGroups["Other"].push(unit);
  }

  if (showNarrativeHeroes == false) {
    delete unitGroups["Narrative Heroes"];
  }

  if (!disableSort) {
    // Order units within their groups
    for (const key of Object.keys(unitGroups)) {
      const group = unitGroups[key];
      unitGroups[key] = orderBy(group, ["size", "cost"], ["desc", "asc"]);
    }
  }
  return unitGroups;
}

export function orderUnits(units: IUnit[], engine: GameEngines, disableSort) {
  const { "Narrative Heroes": narrativeHeroes, ...rest } = getUnitCategories(
    units,
    GameSystems.gf,
    true,
    disableSort,
    engine
  );
  const unitGroups = { ...rest, "Narrative Heroes": narrativeHeroes };
  return Object.values(unitGroups).flat() as IUnit[];
}

export function isError<E>(res: Result.Error<E> | Result.Ok<any>): res is Result.Error<E> {
  return !res.ok;
}

const alpha = env.NEXT_PUBLIC_ENV === "alpha";
const beta = env.NEXT_PUBLIC_ENV === "beta";

export function displayVersion(input: { versionString: string }) {
  if (alpha) return "Labs";
  if (beta) return "Beta";
  if (input && input.versionString) return "v" + input.versionString;
  return null;
}

export function applicationIsResolved(application: {
  statuses: { status: string | RangersStatus }[];
}): boolean {
  const status = application?.statuses?.[0]?.status;
  return status === RangersStatus.Accepted || status === RangersStatus.Declined;
}

export function supportRequestIsResolved(supportRequest: {
  updates: { status: string | RangersStatus }[];
}): boolean {
  const status = supportRequest?.updates?.[0]?.status;
  return status === RangersStatus.Accepted || status === RangersStatus.Declined;
}

export function parseIntOrZero(input: string) {
  const parsed = parseInt(input);
  return isNaN(parsed) ? 0 : parsed;
}
